
import { Component, Mixins } from "vue-property-decorator";
import { Getter } from "vuex-class";
import GenericTable, { TableHeader } from '../table/generic-table.vue';
import GenericUpdateModal, { ModalItem } from '../table/generic-update-modal.vue';
import { AlarmTag} from '@/store/eventqueue/types.ts';
import { PaginatedSearchQueryParams, UserPermissions } from '@/store/types.ts'
import organizationApi from "@/services/api.organizationSettings.ts";
import PaginatedSearch from '@/mixins/PaginatedSearch';
import { namespace } from "vuex-class";
import { numberFormattingMixins } from '@/mixins';
import AreaTreeSelect from "../form/AreaTreeSelect.vue";
import { AreaNode } from "@/types/sv-data/groups/AreaNode";

const GenericTableState = namespace("GenericTable");

@Component({
	components: {
		"generic-table": GenericTable,
		"generic-update-modal": GenericUpdateModal,
	}
})
export default class AlarmTagSetup extends Mixins(PaginatedSearch){
	@Getter("getPermissions") permissions: UserPermissions;
	private alarmTags: AlarmTag[] = [];
	private isLoading: boolean = false;
	private totalRecords: number = 0;
	private paginatedSearchQueryParams: PaginatedSearchQueryParams = null;

	private async beforeMount(): Promise<void> {
		try
		{
			await this.retrieveAlarmTags();
		} catch (e) {
			console.error("Failed to get Alarm Tags ", e);
		}
	}

	private get canEditSiteSetup(): boolean {
		return this.permissions.canEditSiteSetup;
	}

	public async retrieveAlarmTags(): Promise<void> {
		try {
			this.isLoading = true;
			this.alarmTags = [];
			const request = this.generateNewPaginatedSearchRequest(this.paginatedSearchQueryParams);
			request.params.live = true;
			let alarmTagsWithPagination = await organizationApi.retrieveAlarmTags(request);
			this.totalRecords = alarmTagsWithPagination.totalRecords;
			this.alarmTags = alarmTagsWithPagination.data.map(item => {
				return {
					...item,
					alwaysRaise: item.alwaysRaise,
					title: item.title,
					area: item.groupTitle
				}
			});
		}
		catch (ex) {
			console.error("Unexpected error fetching alarm tags: " + ex);
		}
		finally {
			this.isLoading = false;
		}
	}

	private async deleteAlarmTag(alarmTag: AlarmTag): Promise<void> {
		try {
			this.isLoading = true;
			await organizationApi.deleteAlarmTag(alarmTag.responseAlarmTypeID);
		} catch (ex) {
			console.error("Failed to delete Alarm Tag: " + ex);
		} finally {
			this.isLoading = false;
		}

		await this.retrieveAlarmTags();
	}

	private async createAlarmTag(alarmTag: AlarmTag): Promise<void> {
		try {
			this.isLoading = true;
			alarmTag.alwaysRaise = alarmTag.alwaysRaise ?? false;
			await organizationApi.createOrUpdateAlarmTag(alarmTag);
		} catch (ex) {
			console.error("Failed to add alarm tag: " + ex);
		} finally{
			this.isLoading = false;
		}

		await this.retrieveAlarmTags();
	}

	private async updateAlarmTag(alarmTag: AlarmTag): Promise<void> {
		try {
			this.isLoading = true;
			alarmTag.alwaysRaise = alarmTag.alwaysRaise ?? false;
			await organizationApi.createOrUpdateAlarmTag(alarmTag);
		} catch (ex) {
			console.error("Failed to update Alarm Tag: " + ex);
		} finally{
			this.isLoading = false;
		}

		await this.retrieveAlarmTags();
	}
	
	public columns: TableHeader[] = [
		{
			title: "Title",
			key: "title",
			order: 1,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The Alarm Tag Title",
			searchable: true,
			visible: true,
			dataType: "input",
			isTermLabel: true,
			sortable: true,
			isSortedByDefault: true
		},
		{
			title: "Area",
			key: "area",
			order: 2,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The Area the Alarm Tag belongs to",
			searchable: true,
			visible: true,
			dataType: "input",
			isTermLabel: false,
			sortable: true
		},
		{
			title: "Always Raise",
			key: "alwaysRaise",
			order: 3,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "Whether the Alarm Tag is always raised",
			searchable: false,
			visible: true,
			dataType: "checkbox"
		}
	];

	public get modalItems(): ModalItem[] {
		return [
			{
				title: "Title",
				key: "title",
				dataType: "text",
				required: true,
				maxLength: 150,
				placeholder: "Enter the title",
				readOnly: false,
			},
			{
				title: "Area",
				key: "groupId",
				dataType: "component",
				required: true,
				data: AreaTreeSelect,
				props: {
					reduce: (area: AreaNode) => area.id,
					clearable: false,
					appendToBody: true
				},
				readOnlyMethod: (item: ModalItem, row: any) => { return (!this.canEditSiteSetup) },
			},
			{
				title: "Always Raise",
				key: "alwaysRaise",
				dataType: "checkbox",
				required: false,
				readOnly: false,
				description: "Will ignore all auto handled and ignore settings to ensure alarm is always raised."
			}
		]
	}

	private async updatePaginationParameters(paginatedSearchQueryParams?: PaginatedSearchQueryParams): Promise<void> {
		this.paginatedSearchQueryParams = paginatedSearchQueryParams;
		await this.retrieveAlarmTags();
	}
}
